/**
 * Utility formatters for distance, dates, and other display values
 */

/**
 * Distance given in METRES. Named for its unit on purpose: there used to be
 * three functions called formatDistance in this app — this one, one in
 * AgentBriefing taking kilometres, and a third inside TodaysVisits also taking
 * kilometres. Same name, 1000x apart, and nothing stopped a caller picking the
 * wrong one. See formatKm below for the kilometre form.
 */
export const formatMetres = (meters) => {
    if (meters === undefined || meters === null) return '';

    // Convert to km if >= 1000m
    if (meters >= 1000) {
        const km = meters / 1000;
        return `${km.toFixed(1)}km`;
    }

    // Display in meters for short distances
    return `${Math.round(meters)}m`;
};

/**
 * Check if a timestamp is recent (within specified days)
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {number} daysThreshold - Number of days to consider "recent" (default: 7)
 * @returns {boolean} True if timestamp is within threshold
 */
export const isRecent = (timestamp, daysThreshold = 7) => {
    if (!timestamp) return false;

    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= daysThreshold;
};

/**
 * Get days since timestamp
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {number} Number of days since timestamp
 */
export const getDaysSince = (timestamp) => {
    if (!timestamp) return null;

    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Format currency (JPY)
 * @param {number} amount - Amount in yen
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '¥0';
    return `¥${amount.toLocaleString()}`;
};

/**
 * `opportunity_score` is clamped to 0–100 on import but treated as 0–1 in
 * customer_intelligence, so a fractional value used to render as a flat
 * "0% opportunity" — indistinguishable from a real zero. Show a decimal below
 * 1% so a small-but-real score reads as small rather than absent, and treat
 * only null/undefined as missing: a genuine 0 is data, not a gap.
 */
export const formatOpportunity = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  if (n > 0 && n < 1) return `${n.toFixed(1)}%`;
  return `${Math.round(n)}%`;
};

/**
 * The scoring engine writes 999 km as its "no location" sentinel
 * (scoring_engine.py). briefing_generator deliberately never narrates it, so
 * neither do we — an unknown distance shows nothing rather than "999 km".
 */
export const formatKm = (km) => {
  // Number(null) is 0, so a missing distance would otherwise render "0 m away"
  // and claim the lead is at your feet. Absent must stay absent.
  if (km === null || km === undefined || km === '') return null;
  const n = Number(km);
  if (!Number.isFinite(n) || n < 0 || n >= 900) return null;
  if (n < 1) return `${Math.round((n * 1000) / 10) * 10} m`; // nearest 10 m
  return `${n.toFixed(1)} km`;
};
